import { useQuery } from "@tanstack/react-query";
import { CircleAlert, CircleCheck, Clock, Loader2, RotateCcw } from "lucide-react";
import { getCodeLibraryComparison, type ComparisonJob } from "@/api/code_library_compare";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { CommunityChip, ComparisonResult } from "@/components/code_library/ComparisonResult";
import { ComparisonFeedback } from "@/components/code_library/ComparisonFeedback";
import { comparisonQueryKey, useCodeLibraryCompare } from "@/context/CodeLibraryCompareContext";
import { formatMunicipalityLabel } from "@/lib/municipalityLabel";

const POLL_INTERVAL_MS = 1500;

const isRunning = (job?: ComparisonJob) => job?.status === "pending" || job?.status === "processing";

type StepState = "waiting" | "running" | "done";

function ProgressStep({ label, state, detail }: { label: string; state: StepState; detail?: string }) {
  return (
    <li className="flex items-center gap-3 text-sm">
      {state === "done" ? (
        <CircleCheck className="h-4 w-4 shrink-0 text-green-600 dark:text-green-500" aria-hidden />
      ) : state === "running" ? (
        <Loader2 className="h-4 w-4 shrink-0 animate-spin text-primary" aria-hidden />
      ) : (
        <Clock className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
      )}
      <span className={state === "waiting" ? "text-muted-foreground" : undefined}>{label}</span>
      <span className="ml-auto shrink-0 text-xs text-muted-foreground">
        {detail ?? (state === "done" ? "Complete" : state === "running" ? "In progress" : "Waiting")}
      </span>
    </li>
  );
}

/**
 * Progress from the server's own record: each community's research finishes on its own, and
 * the comparison starts only after both.
 */
function ComparisonProgress({ job }: { job: ComparisonJob }) {
  const started = job.status === "processing";
  const homeDone = Boolean(job.progress?.home_researched_at);
  const comparisonDone = Boolean(job.progress?.comparison_researched_at);
  const sideState = (done: boolean): StepState => (done ? "done" : started ? "running" : "waiting");

  return (
    <section className="space-y-4 rounded-lg border bg-card p-4 sm:p-5" aria-labelledby="comparison-progress-heading">
      <h2 id="comparison-progress-heading" className="text-base font-semibold">
        {started ? "Comparison in progress" : "Waiting to start"}
      </h2>
      <ol className="space-y-3" aria-live="polite">
        <ProgressStep
          label={`Researching local laws in ${formatMunicipalityLabel(job.home_municipality)}`}
          state={sideState(homeDone)}
        />
        <ProgressStep
          label={`Researching local laws in ${formatMunicipalityLabel(job.comparison_municipality)}`}
          state={sideState(comparisonDone)}
        />
        <ProgressStep
          label="Analyzing differences and similarities"
          state={homeDone && comparisonDone ? "running" : "waiting"}
          detail={homeDone && comparisonDone ? undefined : "Starts after both"}
        />
      </ol>
      <p className="text-xs text-muted-foreground">
        This usually takes about a minute. You can leave this page; the comparison keeps running and stays in
        your Code Library history.
      </p>
    </section>
  );
}

export function ComparisonSession({ comparisonId }: { comparisonId: number }) {
  const { startNewComparison } = useCodeLibraryCompare();
  const { data: job, isLoading, error } = useQuery({
    queryKey: comparisonQueryKey(comparisonId),
    queryFn: () => getCodeLibraryComparison(comparisonId),
    refetchInterval: (query) => (isRunning(query.state.data) ? POLL_INTERVAL_MS : false),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12" role="status" aria-label="Loading comparison">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !job) {
    return (
      <Alert variant="destructive">
        <CircleAlert className="h-4 w-4" />
        <AlertTitle>This comparison could not be loaded</AlertTitle>
        <AlertDescription>Check your connection and select it again from your history.</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <header className="space-y-3">
        <div className="space-y-1">
          <p className="text-sm font-medium text-muted-foreground">Question</p>
          <h2 className="text-xl font-semibold leading-snug">{job.prompt}</h2>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <CommunityChip side="home" municipality={job.home_municipality} />
          <span className="text-sm text-muted-foreground">&amp;</span>
          <CommunityChip side="comparison" municipality={job.comparison_municipality} />
          <time dateTime={job.created_at} className="ml-1 text-sm text-muted-foreground">
            {new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(
              new Date(job.created_at),
            )}
          </time>
        </div>
      </header>

      {isRunning(job) && <ComparisonProgress job={job} />}

      {job.status === "failed" && (
        <Alert variant="destructive">
          <CircleAlert className="h-4 w-4" />
          <AlertTitle>This comparison could not be completed</AlertTitle>
          <AlertDescription className="space-y-3">
            <p>No results were produced. You can run the same question for the same communities again.</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                startNewComparison({
                  homeMunicipalityId: job.home_municipality.id,
                  comparisonMunicipalityId: job.comparison_municipality.id,
                  prompt: job.prompt,
                })
              }
            >
              <RotateCcw className="mr-2 h-4 w-4" />
              Try again
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {job.status === "completed" && job.result && (
        <>
          <ComparisonResult result={job.result} />
          <ComparisonFeedback comparisonId={job.id} feedbackState={job.feedback_state} />
        </>
      )}
    </div>
  );
}
